import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router/dom'
import { router } from '@/app/routes'
import { AppErrorBoundary } from '@/app/screens/ErrorScreens'
import '@/app/lib/pwa'
import '@/styles/theme.css'

/**
 * Kiosk bootstrap.
 *
 * The WPF app ran maximized with the settings window chromeless and always on
 * top. The web equivalent is fullscreen on first interaction plus a wake lock,
 * since the operator PC sits idle between guests and must not sleep mid-event.
 */

const root = document.getElementById('root')
if (!root) throw new Error('#root missing')

createRoot(root).render(
  <StrictMode>
    {/* Route errors are caught by the error screens in routes.tsx. This catches
        what escapes the router itself, so nothing ends as a blank page. */}
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  </StrictMode>,
)

// Fullscreen needs a user gesture; take the first one and stop listening.
document.addEventListener(
  'pointerdown',
  () => {
    document.documentElement.requestFullscreen?.().catch(() => {
      // Denied or already fullscreen — not worth surfacing.
    })
  },
  { once: true, capture: true },
)

// Keep the screen awake for the length of the event, and re-acquire the lock
// after the OS drops it (which happens on every tab visibility change).
async function keepAwake() {
  try {
    await navigator.wakeLock?.request('screen')
  } catch {
    // Unsupported or blocked; the event PC's power settings are the fallback.
  }
}
void keepAwake()
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void keepAwake()
})
